import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import CookieForm from './CookieForm';
import DarkModeToggle from './DarkModeToggle';
import InstallPWA from './InstallPWA';
import ToggleSwitch from './ToggleSwitch';
import { setCookie, getCookie, convertLength, convertWeight, round, formatNumber, calculateArea, validateAndFormatValue } from '../utils/calculatorUtils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { checkSubscription } from '../utils/subscriptionUtils';
import { getDefaultReference } from '../utils/defaultReferenceConfig';
import ProCalculatorWrapper from './ProCalculatorWrapper';
import ProCalculatorSignInPrompt from './ProCalculatorSignInPrompt';
import '../styles/Calculator.css';
import '../styles/Preview.css';
import AmazonLinksSection from './sections/AmazonLinksSection';

const Calculator = ({ 
  isPreview = false, 
  user = null, 
  isPro = false, 
  isDarkMode = true, 
  setIsDarkMode = () => {}, 
  calculatorVersion = 'simple',
  setCalculatorVersion = () => {},
  activeSection = 'reference',
  setActiveSection = () => {},
  onStateChange = () => {}, 
  savedState = null,
}) => {
  
  const navigate = useNavigate();
  const authContext = useAuth();
  const contextUser = authContext?.user;
  const signOut = authContext?.signOut;
  
  // Use the prop user if provided, otherwise use the context user
  const currentUser = user || contextUser;
  
  // Check if user is admin
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  
  // Check if the device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (currentUser) {
        const subscriptionStatus = await checkSubscription(currentUser.id);
        setIsAdmin(subscriptionStatus.isAdmin || false);
      } else {
        setIsAdmin(false);
      }
    };
    
    checkAdminStatus();
  }, [currentUser]);

  // FIXED UNITS: Length = inches, Weight = grams
  const isMetric = false; 
  const isMetricWeight = true;

  // Get default reference based on fixed units
  const currentDefaultReference = useMemo(() => getDefaultReference(isMetric, isMetricWeight), [isMetric, isMetricWeight]);
  
  // State for calculator inputs
  const [reference, setReference] = useState({ width: '', length: '', weightNoIcing: '', weight: '' });
  const [target, setTarget] = useState({ width: '', length: '', count: '1' });
  const [useDefaultReference, setUseDefaultReference] = useState(false); // Default to not using default
  const [resultText, setResultText] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Refs for calculations
  const referenceAreaRef = useRef(0);
  const referenceIcingPerAreaRef = useRef(0);

  // --- BEGIN REVISED CALCULATION useEffect --- 
  useEffect(() => {
    const currentReference = useDefaultReference ? currentDefaultReference : reference;
    
    // Parse reference values safely
    const refWidth = parseFloat(currentReference.width) || 0;
    const refLength = parseFloat(currentReference.length) || 0;
    const refWeightNoIcing = parseFloat(currentReference.weightNoIcing) || 0;
    const refWeight = parseFloat(currentReference.weight) || 0;

    // Parse target values safely
    const targetWidth = parseFloat(target.width) || 0;
    const targetLength = parseFloat(target.length) || 0;
    const targetCount = parseInt(target.count, 10) || 0;

    // Calculate reference area (assuming rectangle for basic)
    const refArea = calculateArea('rectangle', refWidth, refLength);
    referenceAreaRef.current = refArea;
    
    // Calculate reference icing weight (grams)
    const refIcingWeight = refWeight - refWeightNoIcing;

    // Calculate icing per square unit (grams per square inch)
    let icingPerArea = 0;
    if (refArea > 0 && refIcingWeight > 0) {
      icingPerArea = refIcingWeight / refArea;
    }
    referenceIcingPerAreaRef.current = icingPerArea;

    // Calculate target area (assuming rectangle for basic)
    const targetArea = calculateArea('rectangle', targetWidth, targetLength);

    // Calculate total icing needed (grams)
    const totalIcingGrams = targetArea * icingPerArea * targetCount;

    // Format results
    let newResultText = '';
    if (targetWidth > 0 && targetLength > 0 && targetCount > 0 && icingPerArea > 0) {
      const icingPerTargetGrams = targetArea * icingPerArea;

      // --- Format Total Metric Weight (g, kg/g) ---
      let totalMetricFormatted;
      if (totalIcingGrams >= 1000) {
        const kg = Math.floor(totalIcingGrams / 1000);
        const g = round(totalIcingGrams % 1000, 1); // Round remaining grams
        totalMetricFormatted = `${kg} kg ${g} g (${formatNumber(totalIcingGrams, 2)} g)`;
      } else {
        totalMetricFormatted = `${formatNumber(totalIcingGrams, 2)} g`;
      }

      // --- Format Total Imperial Weight (oz, lb/oz) ---
      const totalIcingOz = totalIcingGrams / 28.34952; // Conversion factor g to oz
      let totalImperialFormatted;
      if (totalIcingOz >= 16) {
        const lb = Math.floor(totalIcingOz / 16);
        const oz = round(totalIcingOz % 16, 1); // Round remaining ounces
        totalImperialFormatted = `${lb} lb ${oz} oz (${formatNumber(totalIcingOz, 2)} oz)`;
      } else {
        totalImperialFormatted = `${formatNumber(totalIcingOz, 2)} oz`;
      }
      
      const refSizeText = `(${formatNumber(refWidth)} x ${formatNumber(refLength)} in)`;
      const targetSizeText = `(${formatNumber(targetWidth)} x ${formatNumber(targetLength)} in)`;
      const weightUnit = 'g'; // Base unit for per-cookie calculations

      newResultText = 
        `Reference Cookie ${useDefaultReference ? '(Default)' : ''}: ${refSizeText}
` + 
        `Icing per sq in: ${formatNumber(icingPerArea, 4)} ${weightUnit}

` + 
        `For ${targetCount} Target Cookie(s): ${targetSizeText}
` + 
        `Each needs: ${formatNumber(icingPerTargetGrams, 2)} ${weightUnit}

` + 
        `Total Needed:
` + 
        `<span class="total-amount">${totalMetricFormatted}</span>
` + 
        `<span class="imperial-weight">${totalImperialFormatted}</span>`;

    } else if (targetWidth > 0 && targetLength > 0 && targetCount > 0 && refArea > 0 && refIcingWeight <= 0) {
      newResultText = 'Reference cookie has zero or negative icing weight.\nCannot calculate icing needed.';
    } else if (targetWidth > 0 && targetLength > 0 && targetCount > 0 && refArea <= 0) {
       newResultText = 'Reference cookie has zero area.\nPlease provide valid reference dimensions.';
    } else {
      newResultText = 'Enter valid dimensions and weights for both reference and target cookies.';
    }

    setResultText(newResultText);

  }, [reference, target, useDefaultReference, currentDefaultReference]); // Dependencies
  // --- END REVISED CALCULATION useEffect --- 

  // Load saved values from cookies - Simplified: Remove unit loading
  const loadSavedValues = () => {
    try {
      const storedReference = getCookie('reference');
      const storedTarget = getCookie('target');
      const storedUseDefault = getCookie('useDefaultReference');

      // Default values
      let parsedReference = { width: '', length: '', weightNoIcing: '', weight: '' };
      let parsedTarget = { width: '', length: '', count: '1' };
      let parsedUseDefault = false;

      // Parse stored values (assumed to be in inches/grams)
      if (storedReference) {
        try {
          parsedReference = JSON.parse(storedReference);
        } catch (e) {
          console.error('Error parsing reference cookie:', e);
        }
      }
      if (storedTarget) {
        try {
          parsedTarget = JSON.parse(storedTarget);
        } catch (e) {
          console.error('Error parsing target cookie:', e);
        }
      }
       if (storedUseDefault) {
        parsedUseDefault = storedUseDefault === 'true';
      }

      setReference(parsedReference);
      setTarget(parsedTarget);
      setUseDefaultReference(parsedUseDefault);

    } catch (error) {
      console.error("Error loading saved values from cookies:", error);
    }
  };

  // Load values on initial mount
  useEffect(() => {
    loadSavedValues();
  }, []);

  // Function to copy results to clipboard
  const handleCopyResults = useCallback(() => {
    if (!resultText) return; // Don't copy if there's no result

    // Basic text formatting (remove HTML tags for clipboard)
    const plainText = resultText.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '');
    
    navigator.clipboard.writeText(plainText)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000); // Show success message for 2 seconds
      })
      .catch(err => {
        console.error('Failed to copy results: ', err);
        // Optionally show an error message to the user
      });
  }, [resultText]);

  // Handle default reference toggle
  const handleDefaultReferenceToggle = (isUsingDefault) => {
    setUseDefaultReference(isUsingDefault);
    // If switching to default, immediately apply fixed default values
    if (isUsingDefault) {
      // No need to get defaults dynamically, they are fixed
      setReference({ 
        width: currentDefaultReference.width, 
        length: currentDefaultReference.length, 
        weightNoIcing: currentDefaultReference.weightNoIcing, 
        weight: currentDefaultReference.weight 
      });
    } else {
       // Optionally clear reference fields or keep last custom values
       // Let's keep the last custom values for now
    }
    setCookie('useDefaultReference', isUsingDefault); // Save toggle state
  };

  // Handle reference change
  const handleReferenceChange = (field, value) => {
    const newReference = { ...reference, [field]: value };
    setReference(newReference);
  };

  // Handle target change
  const handleTargetChange = (field, value) => {
    const newTarget = { ...target, [field]: value };
    setTarget(newTarget);
  };

  // Save values whenever they change
  useEffect(() => {
    setCookie('reference', JSON.stringify(reference));
    setCookie('target', JSON.stringify(target));
  }, [reference, target]);

  const resetAll = () => {
    // Reset to initial state, respecting fixed units
    setReference({ width: '', length: '', weightNoIcing: '', weight: '' });
    setTarget({ width: '', length: '', count: '1' });
    setUseDefaultReference(false);
    setResultText('');
    setCopySuccess(false);
    referenceAreaRef.current = 0;
    referenceIcingPerAreaRef.current = 0;
    
    // Clear cookies
    setCookie('reference', '', 0);
    setCookie('target', '', 0);
    setCookie('useDefaultReference', 'false', 0);

    console.log('Calculator reset.');
  };

  // Render a preview banner if the user is not logged in
  const renderPreviewBanner = () => {
    if (isPreview) {
      return (
        <div className="preview-banner">
          <div className="preview-content">
            <h3>Login Required</h3>
            <p>You need to create a free account to use The Icing Calculator.</p>
            <p>Sign up now to access all basic features!</p>
            <div className="preview-buttons">
              <button 
                className="preview-button login"
                onClick={() => navigate('/login')}
              >
                Login
              </button>
              <button 
                className="preview-button signup"
                onClick={() => {
                  // Navigate to login page with signup tab active
                  setCookie('initialAuthTab', 'signup');
                  navigate('/login');
                }}
              >
                Sign Up Free
              </button>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`calculator ${isDarkMode ? 'dark-mode' : 'light-mode'}`} data-testid="basic-calculator">
      <InstallPWA />
      
      {/* Removed the entire action-bar div */}
      
      {!currentUser && <h1>{calculatorVersion === 'simple' ? 'Basic Calculator' : 'Pro Calculator'}</h1>}
      {renderPreviewBanner()}
      
      {/* Import Settings CSS */}
      <link rel="stylesheet" href="../styles/Settings.css" />
      
      {calculatorVersion === 'simple' ? (
        <>
          <div className="reference-section">
            <div className="section-header">
              <h2 className="section-title">Reference Cookie</h2>
            </div>
            
            <div 
              className="reference-toggle-container" 
              style={{ display: 'flex', alignItems: 'center', gap: '1ch' }} /* Mimic .Toggle style */
            >
              <label htmlFor="defaultReferenceToggle" className="reference-toggle-label">
                Use Default Reference Cookie
              </label>
              <ToggleSwitch 
                switchId="defaultReferenceToggle"
                isOn={useDefaultReference} 
                onToggle={handleDefaultReferenceToggle} 
                disabled={isPreview}
              />
            </div>
            <CookieForm
              hideTitle={true}
              width={useDefaultReference ? currentDefaultReference.width : reference.width}
              length={useDefaultReference ? currentDefaultReference.length : reference.length}
              weightNoIcing={useDefaultReference ? currentDefaultReference.weightNoIcing : reference.weightNoIcing}
              weight={useDefaultReference ? currentDefaultReference.weight : reference.weight}
              unit="in" // Fixed unit
              isMetricWeight={true} // Fixed unit
              onChange={handleReferenceChange}
              disabled={isPreview || useDefaultReference}
            />
          </div>

          <CookieForm
            title="Calculate New Cookies"
            width={target.width}
            length={target.length}
            count={target.count}
            unit="in" // Fixed unit
            isMetricWeight={true} // Fixed unit
            onChange={handleTargetChange}
            disabled={isPreview}
          />

          <div className="button-container">
            <button 
              className="clear-button" 
              onClick={resetAll}
              title="Reset all fields and clear results"
              disabled={isPreview}
            >
              Reset All
            </button>
          </div>

          <div className="results">
            <div className="results-header">
              <h2>Results</h2>
              <button 
                className={`copy-button ${copySuccess ? 'success' : ''}`}
                onClick={handleCopyResults}
                disabled={!resultText || isPreview}
                title={resultText ? "Copy results to clipboard" : "No results to copy"}
              >
                {copySuccess ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p dangerouslySetInnerHTML={{ __html: resultText.split('\n').map(line => line + '<br />').join('') }} />
          </div>

          {/* Amazon Links Section for Basic Calculator */}
          <AmazonLinksSection title="Recommended Decorating Tools" />
        </>
      ) : (
        currentUser ? (
          <ProCalculatorWrapper 
            isDarkMode={isDarkMode} 
            isPreview={isPreview} 
            user={currentUser} 
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            onStateChange={onStateChange} 
            savedState={savedState} 
          />
        ) : (
          <ProCalculatorSignInPrompt />
        )
      )}
    </div>
  );
};

export default Calculator;
